import {
  Box,
  Button,
  Grid,
  Paper,
  Theme,
  Tooltip,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { useFormikContext } from 'formik';
import {
  NewCentralFormType,
  SpecificFilterOption,
  SpecificFilterRow,
} from 'modules/search/types';
import React from 'react';

import { getFilterBody } from './filter-bodies';

import LogicSwitch from 'modules/search/search-logic-switch';
import { relationOptionsOptions } from './relation-options';

import FilterRowMainField from './filter-row-main-field';
import { entityRelationOptions } from './filter-entity-relation-options';
import { mapCategoryIdToSearchEntityType } from 'modules/search/search-utils';
import { getSearchInListForOption } from 'modules/search/v3/search-v3-utils';
import {
  SearchEntityEnum,
  SearchEntityType,
  searchEntityTypeLabel,
} from 'modules/search/v3/search-v3-types';

export const getInitialSpecificFilterRowVals = (
  selectedOption: SpecificFilterOption
): SpecificFilterRow => {
  if (selectedOption.filterType === 'TEXT') {
    return {
      filterOption: selectedOption,
      negation: false,
      textFilterValue: {
        fields: selectedOption.targetFields!,
        field: selectedOption.targetField!,
        value: '',
        textOperation: selectedOption?.defaultTextOperation ?? 'CONTAINS',
      },
    };
  }

  if (selectedOption.filterType === 'CUSTOM') {
    return {
      filterOption: selectedOption,
      negation: false,
      customFilterValues: [
        {
          field: '',
          value: '',
        },
        {
          field: '',
          value: '',
        },
        {
          field: '',
          value: '',
        },
      ],
    };
  }
  if (selectedOption.filterType === 'COMPLEX_TEXT') {
    return {
      filterOption: selectedOption,
      negation: false,
      complexTextFilterValue: {
        fields: selectedOption.targetFieldsComplex!,
        value: '',
        textOperation: selectedOption?.defaultTextOperation ?? 'CONTAINS',
      },
    };
  }

  if (selectedOption.filterType === 'COMPLEX_DATE') {
    return {
      filterOption: selectedOption,
      negation: false,
      complexDateFilterValue: {
        fields: selectedOption.targetFieldsComplex!,
        from: null,
        to: null,
      },
    };
  }

  if (selectedOption.filterType === 'RELATION') {
    return {
      filterOption: selectedOption,
      negation: false,
      relationFilterValue: {
        originator: {
          id: (undefined as unknown) as string,
          label: (undefined as unknown) as string,
          preferredName: '',
        },
        originatorClass: selectedOption.targetRegisterClass,
        relationRole: relationOptionsOptions[0],
      },
    };
  }

  if (selectedOption.filterType === 'REGISTER_CLASSES') {
    return {
      filterOption: selectedOption,
      negation: false,

      // relationFilterValue: {
      //   originatorClass: selectedOption.targetRegisterClass,
      //   relationRole: relationOptionsOptions[0],
      // },
    };
  }

  if (selectedOption.filterType === 'RELATION_V2') {
    return {
      filterOption: selectedOption,
      negation: false,
      relationFilterValue: {
        originator: { id: '', label: '', preferredName: '' },
        originatorClass: selectedOption.targetRegisterClass,
        relationRole: entityRelationOptions[0],
      },
    };
  }

  if (selectedOption.filterType === 'TIME_RANGE') {
    return {
      filterOption: selectedOption,
      negation: false,
      timeRangeFilterValue: { from: null, to: null },
    };
  }
  // fulltext
  return {
    filterOption: selectedOption,
    negation: false,
    ftxFilterValue: { value: '', ftxType: 'FTX' },
  };
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridRoot: {
      width: '100%',
      alignItems: 'end',
      justifyContent: 'space-between',
      margin: '8px 0',
      flexWrap: 'nowrap',
    },
    inputField: {
      marginRight: 16,
    },
  })
);

export const shouldOmitFilter = (
  filterRow: SpecificFilterRow,
  includeSearchEntityTypes: NewCentralFormType['includeSearchEntityTypes']
) => {
  if (!filterRow?.filterOption?.category?.id) {
    return false;
  }
  const type = mapCategoryIdToSearchEntityType(
    filterRow.filterOption.category.id
  );
  if (type) {
    return includeSearchEntityTypes[type] === false;
  }

  return false;
};

export const shouldOmitFilterV3 = (
  option: SpecificFilterOption | undefined,
  includeSearchEntityTypes: NewCentralFormType['includeSearchEntityTypes_V3']
) => {
  if (!option) return { result: false, types: [] };

  const types = getSearchInListForOption(option) as SearchEntityEnum[];
  if (types.length === 0) return { result: false, types };

  if (types.every((t) => !includeSearchEntityTypes[t as SearchEntityType])) {
    return { result: true, types };
  }

  return { result: false, types };
};

function SpecificFilterRow2({
  onRowRemove,
  rowIndex,
}: {
  onRowRemove: (index: number) => void;
  rowIndex: number;
}) {
  const formikCtx = useFormikContext<NewCentralFormType>();
  const classes = useStyles();
  const { values } = formikCtx;
  const body = getFilterBody(values.specificFilterRows[rowIndex]?.filterOption);
  const filterPath = `specificFilterRows[${rowIndex}]`;
  const categoryLabel =
    values.specificFilterRows[rowIndex]?.filterOption?.category?.label;
  const { result: shouldOmit, types: requiredTypes } = shouldOmitFilterV3(
    values.specificFilterRows[rowIndex]?.filterOption,
    values.includeSearchEntityTypes_V3
  );
  return (
    <div style={{ width: '100%' }}>
      {rowIndex > 0 && <LogicSwitch name={`${filterPath}.negation`} />}
      <Paper
        style={{
          width: '100%',
          padding: 16,
          margin: '8px 0',
          border: shouldOmit ? '1px solid red' : 'none',
          background: shouldOmit ? '#ff000008' : 'white',
        }}
      >
        <Box display="flex" justifyContent="space-between">
          {categoryLabel && (
            <Typography
              style={{ fontSize: 12, color: shouldOmit ? 'red' : 'black' }}
            >
              Kategorie: <b>{categoryLabel}</b>
            </Typography>
          )}
          {shouldOmit && (
            <Typography
              style={{
                fontSize: 12,
                color: shouldOmit ? 'red' : 'black',
              }}
            >
              Upozornění: filtr nebude aplikován, filtr vyžaduje povolení
              vyhledávaní alespoň v jednom z: &nbsp;
              <strong>
                {requiredTypes
                  .map((t) => searchEntityTypeLabel[t])
                  .join(', ')
                  .toLocaleUpperCase()}
              </strong>
            </Typography>
          )}
        </Box>
        <Grid container className={classes.gridRoot}>
          <Grid item xs={12} md={3} className={classes.inputField}>
            <FilterRowMainField
              rowIndex={rowIndex}
              formField={`specificFilterRows[${rowIndex}]`}
            />
          </Grid>
          {body(rowIndex, shouldOmit)}
          <Grid item xs="auto">
            <Tooltip
              title={<span style={{ fontSize: 16 }}>Odebrat filtr</span>}
              placement="top"
            >
              {values.specificFilterRows.length === 1 ? (
                <></>
              ) : (
                <Button
                  disabled={values.specificFilterRows.length === 1}
                  variant="contained"
                  color="secondary"
                  onClick={() => onRowRemove(rowIndex)}
                  style={{ minWidth: 40, maxWidth: 40, height: 32, padding: 0 }}
                >
                  <Typography component="span" variant="h4">
                    -
                  </Typography>
                </Button>
              )}
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}
export default SpecificFilterRow2;
